import { 
  Box, 
  Button, 
  Dialog, 
  Typography, 
  Alert, 
  Tooltip,
  BookmarkIcon,
  BookmarkAddedIcon
} from "../utils/mui-imports";
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { trackGAEvent } from '../utils/tracking';

const SaveLookButton = ({ uid, lid, location, saveStatus, isUser = true, guestId = null}) => {
  const [status, setStatus] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saveTooltipOpen, setSaveTooltipOpen] = useState(false);
  const [saveTooltipMessage, setSaveTooltipMessage] = useState('');
  const navigate = useNavigate();
  const redirectURL = useLocation().pathname;

  // Fetch the saved status if the user is logged in and saveStatus is null.
  useEffect(() => {
    if (isUser) {
      if (saveStatus !== null && saveStatus !== undefined) {
        setStatus(saveStatus);
      } else {
        fetch(`/api/looksaves/fetch/${uid}/${lid}`)
          .then((res) => res.json())
          .then((data) => {
            // Assuming the API returns an object with a property "isSaved" indicating the saved status.
            setStatus(data.success);
          })
          .catch((err) => {
            console.error("Error fetching saved status:", err);
            setStatus(false);
          });
      }
    }
  }, [isUser, uid, lid, saveStatus]);

  // Function to handle save/unsave logic.
  const handleButtonClick = () => {
    if (!isUser) {
      // If guest, open the dialog to prompt sign up.
      setDialogOpen(true);
      trackGAEvent('save_look_toggle', { userid: uid, save: status });
      return;
    }

    if (status) {
      // Unsave endpoint.
      fetch("/api/looksaves/unsave", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: uid, look_id: lid }),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Unsave successful:", data);
          setStatus(false);
          setSaveTooltipMessage("Unsaved");
          setSaveTooltipOpen(true);
          setTimeout(() => setSaveTooltipOpen(false), 2000);
        })
        .catch((err) => {
          console.error("Error unsaving the look:", err);
        });
    } else {
      // Save endpoint.
      fetch("/api/looksaves/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: uid, look_id: lid }),
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Save successful:", data);
          setStatus(true);
          setSaveTooltipMessage("Saved");
          setSaveTooltipOpen(true);
          setTimeout(() => setSaveTooltipOpen(false), 2000);
        })
        .catch((err) => {
          console.error("Error saving the look:", err);
        });
    }
  };

  // Icon animation variants.
  const iconVariants = {
    initial: { scale: 1 },
    animate: { scale: [1, 1.2, 1], transition: { duration: 0.2 } },
  };

  // Close the sign-up dialog.
  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      <Tooltip
        arrow 
        title={saveTooltipMessage}
        open={saveTooltipOpen}
        placement="top"
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        <Button
          onClick={handleButtonClick}
          startIcon={
            <motion.div
              key={status ? "saved" : "unsaved"}
              variants={iconVariants}
              initial="initial"
              animate="animate"
            >
              {status ? <BookmarkAddedIcon color="success" /> : <BookmarkIcon />}
            </motion.div>
          }
          sx={{ fontWeight: "bold", fontSize: "0.8rem" }}
        >
          <motion.div
            key={status ? "saved" : "unsaved"}
            variants={iconVariants}
            initial="initial"
            animate="animate"
          >
            {status ? "Saved" : "Save"}
          </motion.div>
        </Button>
      </Tooltip>

      {/* MUI Dialog prompting non-users to sign up */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullScreen
        slotProps={{
          paper: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: 5,
            },
          },
          backdrop: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        }}
        sx={{ zIndex: 2000 }}
      >
        <Box 
          height="100%" 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
        >
          <Alert
            variant="contained"
            severity="error"
            icon={false}
            sx={{ m: 1, borderRadius: 4 }}
          >
            <Typography variant="h3" fontWeight="bold" color="black">
              Sign Up Required
            </Typography>
            <Typography fontWeight="bold" color="black">
              Sign up to save this look to your Look Book.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'right',
                flexDirection: 'row',
                width: '100%',
                p: 2,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  navigate('/signup', {
                    state: { guest_id: guestId, redirect_url: `/showcase/${lid}` }
                  });
                }}
                autoFocus
              >
                Sign Up
              </Button>
              <Button variant="outlined" onClick={handleDialogClose}>
                Cancel
              </Button>
            </Box>
          </Alert>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SaveLookButton;
